// src/pages/UserLoginPage.tsx
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import apiClient from '../api/client';
import { APP_CONFIG } from '../config/constants';

export default function UserLoginPage() {
    const { login, user } = useAuth();
    const navigate = useNavigate();

    // Estados para controlar os campos do formulário e a mensagem de erro
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [emailNotVerified, setEmailNotVerified] = useState(false);
    const [resendingEmail, setResendingEmail] = useState(false);
    const [resendMessage, setResendMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    // Redireciona o usuário se ele já estiver logado
    useEffect(() => {
        if (user) {
            navigate('/conta');
        }
    }, [user, navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setEmailNotVerified(false);
        setResendMessage('');
        setLoading(true);
        try {
            // Chama a função de login do contexto com os dados do formulário
            const data = await login(email, password);
            if (data?.requirePasswordUpdate) {
                navigate('/atualizar-senha-legada');
            } else {
                navigate('/conta'); // Redireciona para a página da conta em caso de sucesso
            }
        } catch (err: any) {
            // Verificar se é erro de email não verificado
            if (err.response?.data?.emailNotVerified) {
                setEmailNotVerified(true);
                setError('E-mail não verificado. Por favor, verifique seu e-mail ou solicite um novo link de verificação abaixo.');
            } else {
                const errorMessage = err.response?.data?.error || err.message || 'Ocorreu um erro.';
                setError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResendVerification = async () => {
        setResendingEmail(true);
        setResendMessage('');
        try {
            const response = await apiClient.post('/auth/resend-verification', { email });
            setResendMessage(response.data.message || 'E-mail de verificação enviado com sucesso!');
        } catch (err: any) {
            // Verificar se é rate limit (429)
            if (err.response?.status === 429 && err.response?.data?.rateLimited) {
                const errorMsg = err.response.data.error || 'Por favor, aguarde antes de solicitar um novo e-mail.';
                const spamMsg = err.response.data.message || 'Verifique sua caixa de entrada e também a pasta de spam/lixo eletrônico.';
                setResendMessage(`⏰ ${errorMsg}\n\n📧 ${spamMsg}`);
            } else {
                setResendMessage(err.response?.data?.error || 'Erro ao reenviar e-mail de verificação.');
            }
        } finally {
            setResendingEmail(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-brand-blue-light via-background to-white flex items-center justify-center p-4">
            <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
                {/* Lado esquerdo - Ilustração/Informação */}
                <div className="hidden lg:flex flex-col justify-center space-y-6 px-8">
                    <div className="space-y-4">
                        <h2 className="text-4xl font-bold text-text-primary leading-tight">
                            Bem-vindo de volta à <span className="text-brand-blue">{APP_CONFIG.storeName}</span>
                        </h2>
                        <p className="text-lg text-text-secondary leading-relaxed">
                            Acesse sua conta para gerenciar seus plugins, visualizar compras e muito mais.
                        </p>
                    </div>

                    {/* Cards de benefícios */}
                    <div className="space-y-4 pt-8">
                        <div className="flex items-start gap-4 bg-white/60 backdrop-blur-sm p-4 rounded-xl shadow-subtle">
                            <div className="flex-shrink-0 w-12 h-12 bg-brand-blue/10 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="font-semibold text-text-primary mb-1">Acesso Instantâneo</h3>
                                <p className="text-sm text-text-secondary">Baixe seus plugins a qualquer momento</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-4 bg-white/60 backdrop-blur-sm p-4 rounded-xl shadow-subtle">
                            <div className="flex-shrink-0 w-12 h-12 bg-brand-blue/10 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="font-semibold text-text-primary mb-1">Seguro e Confiável</h3>
                                <p className="text-sm text-text-secondary">Seus dados estão protegidos conosco</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-4 bg-white/60 backdrop-blur-sm p-4 rounded-xl shadow-subtle">
                            <div className="flex-shrink-0 w-12 h-12 bg-brand-blue/10 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="font-semibold text-text-primary mb-1">Suporte Premium</h3>
                                <p className="text-sm text-text-secondary">Atendimento rápido e eficiente</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Lado direito - Formulário */}
                <div className="w-full">
                    <div className="bg-white p-8 md:p-10 rounded-2xl shadow-subtle-hover">
                        <div className="mb-8">
                            <h1 className="text-3xl font-bold text-text-primary mb-2">Acessar Conta</h1>
                            <p className="text-text-secondary">Entre com suas credenciais</p>
                        </div>

                        {error && (
                            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                                <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                <p className="text-sm text-red-800 flex-1">{error}</p>
                            </div>
                        )}

                        {emailNotVerified && (
                            <div className="mb-6 p-5 bg-yellow-50 border border-yellow-200 rounded-xl">
                                <div className="flex items-start gap-3 mb-4">
                                    <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    <p className="text-sm text-yellow-800 flex-1">
                                        Seu e-mail ainda não foi verificado. Clique no botão abaixo para receber um novo link de verificação.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleResendVerification}
                                    disabled={resendingEmail}
                                    className="w-full bg-yellow-600 text-white py-2.5 px-4 rounded-lg hover:bg-yellow-700 transition-all disabled:bg-gray-400 disabled:cursor-not-allowed font-medium shadow-sm hover:shadow-md"
                                >
                                    {resendingEmail ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            Enviando...
                                        </span>
                                    ) : 'Reenviar E-mail de Verificação'}
                                </button>
                                {resendMessage && (
                                    <div className={`mt-4 p-4 rounded-lg text-sm ${
                                        resendMessage.includes('Erro') 
                                            ? 'bg-red-50 text-red-700 border border-red-200'
                                            : resendMessage.includes('⏰')
                                            ? 'bg-yellow-50 text-yellow-800 border border-yellow-200'
                                            : 'bg-green-50 text-green-700 border border-green-200'
                                    }`}>
                                        {resendMessage.split('\n\n').map((line, index) => (
                                            <p key={index} className={index > 0 ? 'mt-2 font-medium' : ''}>
                                                {line}
                                            </p>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <label htmlFor="email" className="block text-sm font-semibold text-text-primary mb-2">
                                    Email
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <svg className="h-5 w-5 text-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                                        </svg>
                                    </div>
                                    <input
                                        type="email"
                                        id="email"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        required
                                        className="w-full pl-12 pr-4 py-3 border border-border-color rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent transition-all"
                                        placeholder="seuemail@exemplo.com"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="password" className="block text-sm font-semibold text-text-primary mb-2">
                                    Senha
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <svg className="h-5 w-5 text-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                    </div>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        id="password"
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        required
                                        className="w-full pl-12 pr-12 py-3 border border-border-color rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent transition-all"
                                        placeholder="••••••••"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-text-secondary hover:text-text-primary transition-colors"
                                    >
                                        {showPassword ? (
                                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                            </svg>
                                        ) : (
                                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>

                            <div className="flex items-center justify-end">
                                <Link to="/forgot-password" className="text-sm font-medium text-brand-blue hover:text-blue-700 hover:underline transition-colors">
                                    Esqueci minha senha
                                </Link>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-brand-blue text-white py-3.5 rounded-xl font-semibold hover:bg-blue-700 transition-all disabled:bg-gray-400 disabled:cursor-not-allowed shadow-sm hover:shadow-md transform hover:-translate-y-0.5 active:translate-y-0"
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        Entrando...
                                    </span>
                                ) : 'Entrar'}
                            </button>
                        </form>

                        <div className="mt-8 pt-6 border-t border-border-color">
                            <p className="text-center text-sm text-text-secondary">
                                Não tem uma conta?{' '}
                                <Link to="/register" className="font-semibold text-brand-blue hover:text-blue-700 hover:underline transition-colors">
                                    Crie uma agora
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}